/**
 * Round-trips per-block modality scope via `<!-- scope:text,voice -->` HTML
 * comments in the markdown. The editor stores scope as a node attribute on
 * the top-level Tiptap nodes; this module converts between that and the
 * markdown sentinel form that the backend understands (`scopeFilter.js`).
 *
 * Two sentinel positions are supported:
 *
 *   - Block-level: a comment alone on a line, immediately before the block.
 *     Applies to the following block (paragraph, heading, list, blockquote, ...).
 *   - Item-level: a comment immediately after a list marker on the same line.
 *     Applies to that single list item only.
 *
 *     <!-- scope:voice -->
 *     Keep replies short.
 *
 *     - <!-- scope:voice --> Voice-only bullet
 *     - Both-channel bullet
 *     - <!-- scope:text --> Text-only bullet
 */

export const MODALITIES = ['text', 'voice'];

const SCOPE_LINE = /^\s*<!--\s*scope:\s*([^>]+?)\s*-->\s*$/i;
const LIST_MARKER_LINE = /^(\s*)([-*+]|\d+\.)\s+/;
const INLINE_SCOPE = /^(\s*(?:[-*+]|\d+\.)\s+)<!--\s*scope:\s*([^>]+?)\s*-->\s?(.*)$/i;

const normaliseScope = list => {
  const cleaned = (Array.isArray(list) ? list : String(list || '').split(/[\s,]+/))
    .map(s => String(s).trim().toLowerCase())
    .filter(s => MODALITIES.includes(s));
  if (cleaned.length === 0) return null;
  if (cleaned.length === MODALITIES.length) return null;
  return Array.from(new Set(cleaned)).sort();
};

const parseScopeLine = line => {
  const m = line.match(SCOPE_LINE);
  if (!m) return null;
  return normaliseScope(m[1].split(/[\s,]+/));
};

const splitIntoBlocks = md => {
  if (!md) return [];
  const lines = String(md).split('\n');
  const blocks = [];
  let cur = null;
  let leadingBlanks = 0;
  lines.forEach(line => {
    if (line.trim() === '') {
      if (cur) {
        blocks.push(cur);
        cur = null;
      }
      leadingBlanks += 1;
      return;
    }
    if (!cur) {
      cur = { leadingBlanks, lines: [] };
      leadingBlanks = 0;
    }
    cur.lines.push(line);
  });
  if (cur) blocks.push(cur);
  return blocks;
};

const isListBlock = block => block.lines.length > 0 && LIST_MARKER_LINE.test(block.lines[0]);

const stripInlineScope = line => {
  const m = line.match(INLINE_SCOPE);
  if (!m) return { scope: null, line };
  const scope = normaliseScope(m[2].split(/[\s,]+/));
  const rebuilt = `${m[1]}${m[3]}`;
  return { scope, line: rebuilt };
};

/**
 * For a list block, walk each line. Lines starting with a list marker begin a
 * new item; lines without a marker (continuations) belong to the previous
 * item. Returns the stripped lines and a per-item scope array (one entry per
 * top-level item in document order).
 */
const extractListItemScopes = block => {
  const itemScopes = [];
  const outLines = [];
  block.lines.forEach(line => {
    if (LIST_MARKER_LINE.test(line)) {
      const { scope, line: stripped } = stripInlineScope(line);
      itemScopes.push(scope);
      outLines.push(stripped);
    } else {
      outLines.push(line);
    }
  });
  return { lines: outLines, itemScopes };
};

/**
 * Strip scope sentinels out of markdown and return them as a per-block index
 * array (aligned with the resulting block sequence).
 *
 * Each scopes entry is `{ scope, itemScopes? }`. `itemScopes` is present only
 * for list blocks and holds the scope of each top-level list item.
 *
 * @param {string} md
 * @returns {{ stripped: string, scopes: { scope: string[]|null, itemScopes?: (string[]|null)[] }[] }}
 */
export function extractScopesFromMarkdown(md) {
  if (!md) return { stripped: '', scopes: [] };
  const blocks = splitIntoBlocks(md);
  const keptBlocks = [];
  const scopes = [];
  blocks.forEach(block => {
    const lines = [];
    let scope = null;
    block.lines.forEach(line => {
      const parsed = parseScopeLine(line);
      if (parsed === null && SCOPE_LINE.test(line)) {
        return;
      }
      if (parsed) {
        scope = scope ? normaliseScope([...scope, ...parsed]) : parsed;
        return;
      }
      lines.push(line);
    });
    if (lines.length === 0) return;

    const remaining = { leadingBlanks: block.leadingBlanks, lines };
    let itemScopes;
    if (isListBlock(remaining)) {
      const result = extractListItemScopes(remaining);
      remaining.lines = result.lines;
      itemScopes = result.itemScopes;
    }
    keptBlocks.push(remaining);
    scopes.push(itemScopes ? { scope, itemScopes } : { scope });
  });

  const out = [];
  keptBlocks.forEach((block, idx) => {
    if (idx === 0) {
      out.push(block.lines.join('\n'));
      return;
    }
    out.push(`\n${'\n'.repeat(Math.max(1, block.leadingBlanks))}`);
    out.push(block.lines.join('\n'));
  });
  return { stripped: out.join(''), scopes };
}

/**
 * Inject scope sentinels into markdown before each block whose index has a
 * non-default scope value. For list blocks, per-item scopes (when provided
 * via `scopes[i].itemScopes`) are injected inline after the list marker.
 *
 * @param {string} md
 * @param {({ scope: string[]|null, itemScopes?: (string[]|null)[] } | string[] | null)[]} scopes
 * @returns {string}
 */
export function injectScopesIntoMarkdown(md, scopes) {
  if (!md) return '';
  const blocks = splitIntoBlocks(md);
  const out = [];
  blocks.forEach((block, idx) => {
    const entry = scopes?.[idx];
    const rawScope =
      entry && !Array.isArray(entry) && typeof entry === 'object' ? entry.scope : entry;
    const itemScopes =
      entry && !Array.isArray(entry) && typeof entry === 'object' ? entry.itemScopes : undefined;
    const scope = normaliseScope(rawScope);

    let bodyLines = block.lines;
    if (itemScopes && isListBlock({ lines: bodyLines })) {
      let itemIndex = -1;
      bodyLines = bodyLines.map(line => {
        const markerMatch = line.match(LIST_MARKER_LINE);
        if (!markerMatch) return line;
        itemIndex += 1;
        const itemScope = normaliseScope(itemScopes[itemIndex]);
        if (!itemScope) return line;
        const prefix = markerMatch[0];
        const rest = line.slice(prefix.length);
        return `${prefix}<!-- scope:${itemScope.join(',')} -->${rest ? ` ${rest}` : ''}`;
      });
    }

    const blockText = bodyLines.join('\n');
    const prefix = idx === 0 ? '' : `\n${'\n'.repeat(Math.max(1, block.leadingBlanks))}`;
    if (scope) {
      out.push(prefix);
      out.push(`<!-- scope:${scope.join(',')} -->\n`);
      out.push(blockText);
    } else {
      out.push(prefix);
      out.push(blockText);
    }
  });
  return out.join('');
}

/**
 * Toggle a modality membership for a current scope, returning the new scope
 * (or `null` when the result equals "both modalities").
 */
export function toggleScopeModality(currentScope, modality) {
  if (!MODALITIES.includes(modality)) return currentScope;
  const current =
    Array.isArray(currentScope) && currentScope.length ? currentScope : [...MODALITIES];
  const has = current.includes(modality);
  let next;
  if (has) {
    next = current.filter(m => m !== modality);
    if (next.length === 0) {
      // Don't allow turning everything off; flip to the other modality only.
      next = MODALITIES.filter(m => m !== modality);
    }
  } else {
    next = [...current, modality];
  }
  return normaliseScope(next);
}

export function scopeIncludes(scope, modality) {
  if (!Array.isArray(scope) || scope.length === 0) return true;
  return scope.includes(modality);
}
